import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext.js'
import { useState, useEffect } from 'react'
import {
  ClipboardList, ShieldCheck, HelpCircle, FileText, Globe,
  BookOpen, Building2, Settings, Workflow, Type, SlidersHorizontal,
  ChevronLeft, ChevronRight, LogOut, Image, BarChart2, Monitor, Phone, Moon, Users, Car, Activity, Palette, Tablet, ListChecks, UserCheck, MapPin, LayoutGrid,
  Truck,
} from 'lucide-react'
import { useHolidayReview, holidayReviewCount } from '../utils/holidayReview.js'
import './Sidebar.css'

// adminOnly: true  — hidden from 'user' role (kiosk-affecting pages)
// ownerOnly: true  — hidden from everyone except owner
const NAV_ITEMS = [
  { path: '/',                 label: 'Check-ins',            Icon: ClipboardList,     ownerOnly: false, adminOnly: false, featureFlag: null },
  { path: '/lpr-events',      label: 'Kennzeichen-Scans',    Icon: Activity,          ownerOnly: false, adminOnly: false, featureFlag: 'lpr_enabled' },
  // The single surface for "where does this visitor go": the pending list, the
  // occupancy grid and who is currently on site. adminOnly is false because assigning
  // and releasing is reception's daily job and includes the 'user' role — only
  // configuring destinations (below) and authoring rules (Flow Builder) are restricted.
  //
  // No featureFlag: it is useful to a tenant with destinations AND to one that only
  // assigns info pages manually, so visibility is the OR of those two conditions
  // (see requiresDestinationsOrDynamicInfo in the filter below). A single flag here
  // would hide the page from half the tenants it serves.
  { path: '/destination-board', label: 'Disposition',         Icon: LayoutGrid,        ownerOnly: false, adminOnly: false, featureFlag: null, requiresDestinationsOrDynamicInfo: true },
  { path: '/destinations',     label: 'Zielorte',             Icon: MapPin,            ownerOnly: false, adminOnly: true,  featureFlag: 'destinations_enabled' },
  { path: '/rules',            label: 'Sicherheitsregeln',    Icon: ShieldCheck,       ownerOnly: false, adminOnly: true,  featureFlag: 'safety_rules_enabled' },
  { path: '/quiz',             label: 'Quiz',                 Icon: HelpCircle,        ownerOnly: false, adminOnly: true,  featureFlag: 'allow_quiz' },
  { path: '/info-pages',       label: 'Informationsseiten',   Icon: FileText,          ownerOnly: false, adminOnly: true,  featureFlag: 'info_pages_enabled' },
  { path: '/languages',        label: 'Sprachen',             Icon: Globe,             ownerOnly: false, adminOnly: true,  featureFlag: 'allow_language_management' },
  { path: '/rule-library',     label: 'Regelbibliothek',      Icon: BookOpen,          ownerOnly: true,  adminOnly: false, featureFlag: null },
  { path: '/tenants',          label: 'Mandanten',            Icon: Building2,         ownerOnly: true,  adminOnly: false, featureFlag: null },
  { path: '/stations',         label: 'Stationen',            Icon: Tablet,            ownerOnly: true,  adminOnly: false, featureFlag: null },
  { path: '/tenant-features',  label: 'Funktionen',           Icon: Settings,          ownerOnly: true,  adminOnly: false, featureFlag: null },
  { path: '/flow-builder',     label: 'Flow Builder',         Icon: Workflow,          ownerOnly: true,  adminOnly: false, featureFlag: null },
  { path: '/welcome-screen',   label: 'Willkommensbildschirm', Icon: Monitor,          ownerOnly: false, adminOnly: true,  featureFlag: 'welcome_screen_enabled' },
  { path: '/documents',        label: 'Dokumente',            Icon: Image,             ownerOnly: false, adminOnly: false, featureFlag: 'documents_enabled' },
  { path: '/daily-report',     label: 'Tagesbericht',         Icon: BarChart2,         ownerOnly: false, adminOnly: false, featureFlag: 'daily_report_enabled' },
  { path: '/translations',     label: 'UI-Texte',             Icon: Type,              ownerOnly: false, adminOnly: true,  featureFlag: 'translations_enabled' },
  { path: '/sip-settings',     label: 'Telefonkontakte',      Icon: Phone,             ownerOnly: false, adminOnly: true,  featureFlag: 'sip_enabled' },
  { path: '/standby-settings', label: 'Standby-Bildschirm',  Icon: Moon,              ownerOnly: false, adminOnly: true,  featureFlag: 'standby_settings_enabled' },
  { path: '/license-plates',   label: 'Kennzeichen',          Icon: Car,               ownerOnly: false, adminOnly: true,  featureFlag: 'license_plates_enabled' },
  { path: '/allowlist',        label: 'Personenzulassung',    Icon: ListChecks,        ownerOnly: false, adminOnly: true,  featureFlag: 'allowlist_enabled' },
  { path: '/lieferavis',       label: 'Lieferavis',           Icon: Truck,             ownerOnly: false, adminOnly: true,  featureFlag: 'deliveries_enabled', featureMustBeOn: true },
  { path: '/theme-settings',   label: 'Darstellung',         Icon: Palette,           ownerOnly: false, adminOnly: true,  featureFlag: 'theme_settings_enabled' },
  { path: '/users',            label: 'Benutzerverwaltung',   Icon: Users,             ownerOnly: false, adminOnly: true,  featureFlag: 'users_enabled' },
  { path: '/settings',         label: 'Einstellungen',        Icon: SlidersHorizontal, ownerOnly: false, adminOnly: false, featureFlag: null },
]

const ROLE_LABEL = {
  owner:        'Service Owner',
  tenant_admin: 'Admin',
  user:         'Benutzer',
}

const Sidebar = ({ collapsed = false, onToggle }) => {
  const { admin, logout, isOwner, features, apiFetch } = useAuth()
  const navigate = useNavigate()
  const [pendingCount, setPendingCount] = useState(0)
  // Whether any flow actually uses a dynamic info page. Starts null = unknown, so the
  // entry is simply not shown until we know, rather than flickering in and out.
  const [dynamicInfoInUse, setDynamicInfoInUse] = useState(null)

  // Fetched once rather than polled: flow configs change rarely, and a page reload
  // picks up a change. Owners are not checked — they administer every tenant, so there
  // is no single tenant whose flows would decide this for them.
  useEffect(() => {
    if (!admin || isOwner) return
    if (!admin.tenant_id) { setDynamicInfoInUse(false); return }
    apiFetch(`/api/admin/live-sessions/${admin.tenant_id}/in-use`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setDynamicInfoInUse(d ? !!d.inUse : true))  // fail open
      .catch(() => setDynamicInfoInUse(true))
  }, [admin, isOwner, apiFetch])

  useEffect(() => {
    if (!admin) return
    let id
    const fetchCount = () => {
      apiFetch('/api/admin/pending-count')
        .then(r => {
          if (r.status === 401) { clearInterval(id); return null }
          return r.json()
        })
        .then(d => d && setPendingCount(d.count || 0))
        .catch(() => {})
    }
    fetchCount()
    id = setInterval(fetchCount, 30000)
    return () => clearInterval(id)
  }, [admin])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const isUser = admin?.role === 'user'

  // Holidays waiting for a decision — the same list the toast shows. Not for the
  // 'user' role, which can't open the settings page.
  const holidayReview = useHolidayReview(apiFetch, !!admin && !isUser)
  const holidayReviewBadge = holidayReviewCount(holidayReview)

  const visibleItems = NAV_ITEMS.filter(item => {
    if (item.ownerOnly && !isOwner) return false
    if (item.adminOnly && isUser) return false
    if (item.featureFlag && !isOwner && features[item.featureFlag] === false) return false
    // /me sends the tenant's stored features without defaults, so a flag added after a
    // tenant was last saved is simply missing — and "not false" counts as on above.
    // Features that are off by default must therefore require an explicit true.
    if (item.featureMustBeOn && !isOwner && features[item.featureFlag] !== true) return false
    // The board serves three kinds of tenant, so any condition is enough: destinations
    // are enabled, some flow actually uses a dynamic info page, or the tenant operates
    // its barriers from here (the barrier strip sits on this page). With none, the page
    // could only ever be empty. Owners always see it, as with featureFlag above.
    // The kiosk display buttons sit there too, so they count as well.
    if (item.requiresDestinationsOrDynamicInfo && !isOwner) {
      const hasDestinations = features.destinations_enabled === true
      const hasBarrierControl = features.barrier_control_enabled === true
      const hasDisplayControl = features.kiosk_display_control_enabled === true
      if (!hasDestinations && !hasBarrierControl && !hasDisplayControl && dynamicInfoInUse !== true) return false
    }
    return true
  })

  return (
    <aside className={`sidebar${collapsed ? ' sidebar--collapsed' : ''}`}>
      {/* Logo + toggle */}
      <div className="sidebar-logo">
        <span className="sidebar-logo-mark">v</span>
        {!collapsed && (
          <div className="sidebar-logo-text">
            <div className="sidebar-logo-name">viGuard</div>
            <div className="sidebar-logo-sub">Admin Dashboard</div>
          </div>
        )}
        <button
          className="sidebar-toggle"
          onClick={onToggle}
          title={collapsed ? 'Sidebar aufklappen' : 'Sidebar zuklappen'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {visibleItems.map(({ path, label, Icon }) => (
          <NavLink
            key={path}
            to={path}
            end={path === '/'}
            className={({ isActive }) => `sidebar-nav-item${isActive ? ' active' : ''}`}
            title={collapsed ? label : undefined}
          >
            <span className="sidebar-nav-icon"><Icon size={17} strokeWidth={1.8} /></span>
            {!collapsed && <span className="sidebar-nav-label">{label}</span>}
            {path === '/' && pendingCount > 0 && (
              <span className="sidebar-pending-badge">{pendingCount}</span>
            )}
            {path === '/standby-settings' && holidayReviewBadge > 0 && (
              <span className="sidebar-pending-badge sidebar-pending-badge--todo" title="Feiertage zu prüfen">
                {holidayReviewBadge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User footer */}
      <div className="sidebar-footer">
        {!collapsed && (
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">
              {admin?.email?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-email">{admin?.email}</div>
              <div className="sidebar-user-role">{ROLE_LABEL[admin?.role] || admin?.role}</div>
            </div>
          </div>
        )}
        <button className="sidebar-logout" onClick={handleLogout} title="Abmelden">
          <LogOut size={15} />
        </button>
      </div>
    </aside>
  )
}

export default Sidebar
